import { useMemo } from "react";
import { Car, ReceiptText, Truck, Users, ClipboardCheck, AlertTriangle, Plus, Phone } from "lucide-react";
import { useDashboardCache } from "./hooks/useDashboardCache";
import { useDashboardPrefs } from "./hooks/useDashboardPrefs";
import { DashboardHeader } from "./components/DashboardHeader";
import { QuickActionsRow } from "./components/QuickActionsRow";
import { KpiCard, KpiCardSkeleton } from "./components/KpiCard";
import { NeedsAttention } from "./components/NeedsAttention";
import { WorkshopLoadDonut } from "./components/WorkshopLoadDonut";
import { JobsTrendChart } from "./components/JobsTrendChart";
import { ActiveJobsTable } from "./components/ActiveJobsTable";
import { TodaysAppointmentsStrip } from "./components/TodaysAppointmentsStrip";

/**
 * AdvisorDashboard — spec-lite variant for Service Advisor.
 * Uses the same building blocks; role-specific KPIs and actions.
 */
export default function AdvisorDashboard() {
  const [prefs, patchPrefs] = useDashboardPrefs();

  const params = useMemo(() => {
    const p = {};
    if (prefs.range === "custom" && prefs.from_date && prefs.to_date) {
      p.range = "custom";
      p.from_date = prefs.from_date;
      p.to_date = prefs.to_date;
    } else {
      p.range = prefs.range || "today";
    }
    return p;
  }, [prefs]);

  const trendDays = prefs.range === "month" ? 28 : 7;
  const summary = useDashboardCache("fender_adv_summary", "/dashboard/summary", { params });
  const load = useDashboardCache("fender_adv_load", "/dashboard/workshop-load");
  const trend = useDashboardCache("fender_adv_trend", "/dashboard/jobs-trend", { params: { days: trendDays } });
  const jobs = useDashboardCache("fender_adv_jobs", "/dashboard/active-jobs", { params: { limit: 8 } });
  const attention = useDashboardCache("fender_adv_attention", "/dashboard/action-centre");

  const reloadAll = () => [summary, load, trend, jobs, attention].forEach((h) => h.reload());
  const loading = summary.loading;

  const s = summary.data || {};
  const kpis = [
    { key: "inspections", label: "Vehicles to Receive", value: (s.received_today ?? 0), hint: "New check-ins today", Icon: Car, tone: "frosted", to: "/job-cards", testId: "adv-kpi-recv" },
    { key: "inspection_pending", label: "Inspections Pending", value: s.vehicles_received ?? 0, hint: "Awaiting inspection or start", Icon: ClipboardCheck, tone: "steel", to: "/job-cards?status=vehicle_received", testId: "adv-kpi-insp" },
    { key: "est_approval", label: "Estimates Awaiting", value: s.estimates_awaiting ?? 0, hint: "Chase customer approval", Icon: ReceiptText, tone: "orange", to: "/job-cards?status=approval_pending", testId: "adv-kpi-est" },
    { key: "followups", label: "Customer Follow-ups", value: (s.ready_pending_payment ?? 0) + (s.delayed_parts ?? 0), hint: "Ready + delayed cases", Icon: Phone, tone: "steel", to: "/customers", testId: "adv-kpi-followup" },
    { key: "ready", label: "Ready for Delivery", value: s.ready_for_delivery ?? 0, hint: `${s.ready_pending_payment ?? 0} payments pending`, Icon: Truck, tone: "green", to: "/job-cards?status=ready_for_delivery", testId: "adv-kpi-ready" },
    { key: "delayed", label: "Delayed Jobs", value: (s.delayed_parts ?? 0), hint: "Behind expected delivery", Icon: AlertTriangle, tone: "red", to: "/job-cards?view=delayed", testId: "adv-kpi-delayed" },
  ];

  const quickActions = [
    { key: "create-jc", label: "Create Job Card", Icon: Plus, url: "/job-cards/new", tone: "primary" },
    { key: "create-est", label: "Create Estimate", Icon: ReceiptText, url: "/estimates/new" },
    { key: "contact-customer", label: "Contact Customer", Icon: Users, url: "/customers" },
    { key: "deliver-vehicle", label: "Deliver Vehicle", Icon: Truck, url: "/job-cards?status=completed" },
  ];

  return (
    <div className="space-y-6" data-testid="advisor-dashboard-v3">
      <DashboardHeader
        title="Advisor Desk"
        range={prefs.range}
        fromDate={prefs.from_date}
        toDate={prefs.to_date}
        onRangeChange={(k) => patchPrefs({ range: k, from_date: null, to_date: null })}
        onCustomRange={(f, t) => patchPrefs({ range: "custom", from_date: f, to_date: t })}
        onRefresh={reloadAll}
        lastSyncedAt={summary.lastSyncedAt}
        loading={loading}
      />
      <QuickActionsRow actions={quickActions} />
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="adv-kpi-grid">
        {kpis.map(({ key, ...rest }) =>
          summary.loading && !summary.data ? <KpiCardSkeleton key={key} /> : <KpiCard key={key} {...rest} />
        )}
      </div>
      <TodaysAppointmentsStrip />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2">
          <WorkshopLoadDonut segments={load.data?.segments || []} total={load.data?.total || 0} />
        </div>
        <div className="lg:col-span-3">
          <JobsTrendChart series={trend.data?.series || []} days={trendDays} />
        </div>
      </div>
      {/* Active Jobs above Needs Attention, matching Owner Dashboard order */}
      <ActiveJobsTable rows={jobs.data?.rows || []} />
      <NeedsAttention items={attention.data?.items || []} />
    </div>
  );
}
